import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useUkvdLookup, { UkvdVehicleRow } from "../../../data/useVehicleLookup";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import { TextInput } from "../../shared/Inputs/TextInput";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";
import UkvdResultsTable from "./VehicleResultsTable";
import { NumberInput } from "../../shared/Inputs/NumberInput";
import GeneralPriceSettingSingle from "../PriceConfiguration/GeneralPriceSettingSingle";
import SwitchInput from "../../shared/Inputs/SwitchInput";
import usePriceSettingsMutations from "../../../data/usePriceSettingsMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../../components/notifications/useNotifications";
import Divider from "../../shared/Divider";

type FormVals = { make: string; range: string; engineCode?: string };

// Mirror of backend DTO (CreateGenericPriceSettingRequest)
type CreateGenericPriceSettingRequest = {
  make?: string | null;
  model?: string | null;              // using RANGE here per your backend
  fromYear?: number | null;
  toYear?: number | null;
  fuelType?: string | null;
  engineCode?: string | null;         // TODO when available
  fromEngineCC?: number | null;
  toEngineCC?: number | null;
  customerPriceConfigurationId: string; // Guid as string is fine
  priceSetting: { value: number; isFixed: boolean }; // UpdatePriceSettingRequest
};

export default function UkvdSearch({
  customerId,
  onSuccess,
}: {
  customerId: string;
  onSuccess: () => void;
}) {
  const { addNotification } = useNotification();
  const {
    createSpecific,
    createBulkSpecific,            // <-- use bulk endpoint
  } = usePriceSettingsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Price Adjustments");

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormVals>({
    defaultValues: { make: "", range: "", engineCode: "" },
  });

  const selectedMake = watch("make");
  const selectedRange = watch("range");

  const { makes, ranges, search } = useUkvdLookup(customerId, selectedMake || undefined);
  const [results, setResults] = useState<UkvdVehicleRow[]>([]);

  // selection + marked list
  const [selectedRows, setSelectedRows] = useState<UkvdVehicleRow[]>([]);
  const [clearSelectionSignal, setClearSelectionSignal] = useState(0);
  const [markedVehicles, setMarkedVehicles] = useState<UkvdVehicleRow[]>([]);

  const makeOptions = useMemo(
    () => (makes.data ?? []).map((m) => ({ id: m, name: m })),
    [makes.data]
  );
  const rangeOptions = useMemo(
    () => (ranges.data ?? []).map((r) => ({ id: r, name: r })),
    [ranges.data]
  );

  useEffect(() => {
    // clear range when make changes
    setValue("range", "");
  }, [selectedMake, setValue]);

  const onSubmit = (data: FormVals) => {
    if (!data.make || !data.range) return;
    search.mutate(
      { make: data.make, range: data.range, engineCode: data.engineCode },
      {
        onSuccess: (rows) => {
          setResults(rows);
          setSelectedRows([]);
          setClearSelectionSignal((n) => n + 1);
        },
      }
    );
  };

  // Helpers to manage marked list
  const markForCreation = () => {
    if (selectedRows.length === 0) return;
    const byUvc = new Map<string, UkvdVehicleRow>();
    markedVehicles.forEach((v) => v.uvc && byUvc.set(v.uvc, v));
    selectedRows.forEach((v) => v.uvc && byUvc.set(v.uvc, v));
    setMarkedVehicles(Array.from(byUvc.values()));
    setSelectedRows([]);
    setClearSelectionSignal((n) => n + 1);
  };
  const clearSelection = () => {
    setSelectedRows([]);
    setClearSelectionSignal((n) => n + 1);
  };
  const clearMarked = () => setMarkedVehicles([]);

  // --- BULK CREATE from marked rows ---------------------------------------
  const onPriceSubmit = (data: any) => {
    // Normalize priceSetting
    const priceVal = parseFloat(data?.priceSetting?.value ?? "0");
    const isFixed =
      data?.priceSetting?.isFixed === true ||
      data?.priceSetting?.isFixed === "true";

    const priceSetting = { value: priceVal, isFixed };

    // If we have marked rows -> bulk create; else fallback to single create
    if (markedVehicles.length > 0) {
      const items: any[] = markedVehicles.map((v) => {
        // derive years from dates (if present)
        const fromYear =
          v.startDate ? new Date(v.startDate).getFullYear() : null;
        const toYear = v.endDate ? new Date(v.endDate).getFullYear() : null;

        // use RANGE for model (per your backend mapping)
        return {
          make: v.make ?? null,
          model: v.range ?? null,
          fromYear,
          toYear,
          fuelType: v.fuelType ?? null,
          engineCode: null, // TODO when engine codes arrive
          fromEngineCC: v.engineCapacityCC ?? null,
          toEngineCC: v.engineCapacityCC ?? null,
          customerPriceConfigurationId: customerId,
          transmissionType: v.transmissionType,
          maximumPrice: data?.maxPrice ? parseFloat(data.maxPrice) : null,
          priceSetting,
        };
      });

      if (items.length === 0) {
        addNotification({
          variant: "error",
          primaryText: "Nothing to create",
        });
        return;
      }

      createBulkSpecific.mutate(items, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          clearMarked();
          reset();
          onSuccess();
        },
      });

      return;
    }

    // ---- Single create (manual) fallback ---------------------------------
    // You already had this workflow; keep behavior
    if (!selectedMake && !selectedRange) {
      addNotification({
        variant: "error",
        primaryText:
          "Please either mark vehicles or fill at least Make/Range for a single create.",
      });
      return;
    }

    const singlePayload = {
      id: null,
      customerPriceConfigurationId: customerId,
      make: selectedMake || null,
      model: selectedRange || null, // using Range as “model”
      fromYear: null,
      toYear: null,
      fuelType: data?.fuelType ?? null,
      engineCode: data?.engineCode ?? null,
      fromEngineCc: data?.fromEngineCc ? parseInt(String(data.fromEngineCc)) : null,
      toEngineCc: data?.toEngineCc ? parseInt(String(data.toEngineCc)) : null,
      priceSetting,
      // maxPrice / partsLookup are not in CreateGenericPriceSettingRequest; omitted on purpose
    };

    createSpecific.mutate(singlePayload as any, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        reset();
        onSuccess();
      },
    });
  };

  const rangeDisabled = !selectedMake;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Vehicle Lookup</h1>

      {/* Search form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ComboBoxInput
            label="Make"
            options={makeOptions}
            searchField="name"
            valueField="name"
            register={register}
            setValue={setValue}
            registerName="make"
          />

          <div className={rangeDisabled ? "opacity-50 pointer-events-none" : ""}>
            <ComboBoxInput
              label="Range"
              options={rangeOptions}
              searchField="name"
              valueField="name"
              register={register}
              setValue={setValue}
              registerName="range"
            />
          </div>

          <TextInput label={"Engine Code"} registerName={`engineCode`} register={register} />
        </div>

        <div className="pt-2 flex gap-3">
          <ButtonInput label={"Search"} isSubmit />
        </div>
      </form>

      {/* Results toolbar */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Results: <span className="font-medium">{results.length}</span>
          {selectedRows.length > 0 && (
            <span className="ml-3">
              Selected: <span className="font-medium">{selectedRows.length}</span>
            </span>
          )}
          {markedVehicles.length > 0 && (
            <span className="ml-3">
              Marked: <span className="font-medium">{markedVehicles.length}</span>
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={markForCreation}
            disabled={selectedRows.length === 0}
            className={`px-3 h-9 rounded-md text-sm font-medium ${
              selectedRows.length === 0
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-primary-600 text-white hover:bg-primary-700"
            }`}
          >
            Mark for creation {selectedRows.length > 0 ? `(${selectedRows.length})` : ""}
          </button>

          <button
            type="button"
            onClick={clearSelection}
            className="px-3 h-9 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200"
          >
            Clear selection
          </button>
        </div>
      </div>

      {/* Results table */}
      <div className="mt-3">
        {search.isLoading && <div className="text-sm text-gray-500">Searching…</div>}
        {!search.isLoading && (
          <UkvdResultsTable
            rows={results}
            onSelectionChange={setSelectedRows}
            clearSelectionSignal={clearSelectionSignal}
          />
        )}
      </div>

      {/* Marked list */}
      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Marked for creation</h2>
          <button
            type="button"
            onClick={clearMarked}
            className="px-3 h-9 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200"
          >
            Clear marked
          </button>
        </div>

        {markedVehicles.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">Nothing marked yet.</div>
        ) : (
          <div className="mt-2 max-h-48 overflow-auto border rounded-md">
            <ul className="divide-y">
              {markedVehicles.map((v) => (
                <li key={v.uvc ?? `${v.make}|${v.range}|${v.model}`} className="px-3 py-2 text-sm">
                  <span className="font-medium">{v.uvc}</span>
                  <span className="text-gray-500 ml-2">
                    {v.make} / {v.range} {v.model ? `— ${v.model}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Pricing form – submits BULK (if marked) or single otherwise */}
      <form onSubmit={handleSubmit(onPriceSubmit)} className="space-y-4">
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 items-center gap-x-4 gap-y-2">
          {/* ------- Fixed Prices ------- */}
          <Divider className="mt-4 col-span-2 sm:col-span-4">
            Fixed Prices (Overrides Price Change)
          </Divider>
  
          {/* <NumberInput label={"Fixed Price"} registerName={`fixedPrice`}           register={register} minVal={0} /> */}
          <NumberInput label={"Good Engine"} registerName={`goodEngineFixedPrice`} register={register} minVal={0} />
          <NumberInput label={"Minor Fault"} registerName={`minorFaultFixedPrice`} register={register} minVal={0} />
          <NumberInput label={"Major Fault"} registerName={`majorFaultFixedPrice`} register={register} minVal={0} />
          {/* ---------------------------- */}
  
          {/* ------- Price Changes ------- */}
          <Divider className="mt-4 col-span-2 sm:col-span-4">
            Price Changes (If no Fixed Price set)
          </Divider>
  
          {/* <GeneralPriceSettingSingle label={"Price Change"} objectName={"priceSetting"}           register={register} setValue={setValue} watch={watch} minVal={null} /> */}
          <GeneralPriceSettingSingle label={"Good Engine"}  objectName={"goodEnginePriceSetting"} register={register} setValue={setValue} watch={watch} minVal={null} />
          <GeneralPriceSettingSingle label={"Minor Fault"}  objectName={"minorFaultPriceSetting"} register={register} setValue={setValue} watch={watch} minVal={null} />
          <GeneralPriceSettingSingle label={"Major Fault"}  objectName={"majorFaultPriceSetting"} register={register} setValue={setValue} watch={watch} minVal={null} />
  
          {/* ----------------------------- */}

          <NumberInput label={"Maximum Price"} registerName={`maxPrice`} register={register} minVal={0} />

          <div className="mt-6 flex justify-center">
            <SwitchInput
              label={"Parts Lookup"}
              registerName={`partsLookup`}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </div>

          <div className="mt-6 flex justify-center">
            <SwitchInput
              label={"Is Active"}
              registerName={`isActive`}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          </div>

          <div className="flex gap-x-4 h-10 mt-[36px]">
            <ButtonInput
              label={
                markedVehicles.length > 0
                  ? `Create from marked (${markedVehicles.length})`
                  : "Create"
              }
              isSubmit
            />
          </div>
        </div>
      </form>
    </div>
  );
}
